import { Card, Col, Form, Input, Row, Select, Switch } from 'antd'
import { FormInstance } from 'antd/lib'

import { OtherInstitutionType } from 'src/api/requests'
import { IDNP_REGEX, PHONE_REGEX, requiredRules } from 'src/app-constants'
import { CountrySelect, CUATMTreeSelect, RegionSelect } from 'src/components'
import { useLocales } from 'src/locales'

const { Item } = Form

type Props = {
  form: FormInstance
  hasId?: boolean
  isDisabled?: boolean
}
export const OtherInstitutionsDetails = ({
  form,
  hasId,
  isDisabled = false
}: Props) => {
  const { t } = useLocales()

  const status = Form.useWatch('status', form)

  const region = Form.useWatch(['address', 'region'], form)
  const regionId = Form.useWatch(['address', 'regionId'], form)

  const otherInstitutionOptions = Object.values(OtherInstitutionType).map(
    (type) => ({
      label: t(`common.${type}`),
      value: type
    })
  )

  return (
    <Card>
      <Form
        layout="vertical"
        form={form}
        requiredMark={false}
        disabled={isDisabled}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12} xl={8}>
            <Item
              name="idno"
              label={t('organizational_structure.idno')}
              rules={requiredRules?.concat({
                pattern: IDNP_REGEX,
                message: t('organizational_structure.idno_error')
              })}
            >
              <Input
                placeholder={t('organizational_structure.idno')}
                disabled={hasId}
              />
            </Item>
          </Col>

          <Col xs={24} md={12} xl={8}>
            <Item
              name="name"
              label={t('organizational_structure.name')}
              rules={requiredRules}
            >
              <Input
                placeholder={t('organizational_structure.name')}
                disabled={hasId}
              />
            </Item>
          </Col>

          <Col xs={24} md={12} xl={8}>
            <Item
              name="fiscalCode"
              label={t('other_institutions.fiscal_code')}
              rules={requiredRules}
            >
              <Input
                placeholder={t('other_institutions.fiscal_code')}
                disabled={hasId}
              />
            </Item>
          </Col>

          <Col xs={24} md={12} xl={8}>
            <Item
              name={['contactInfo', 'phone']}
              label={t('organizational_structure.phone')}
              rules={requiredRules?.concat({
                pattern: PHONE_REGEX,
                message: t('organizational_structure.phone_error')
              })}
            >
              <Input placeholder={t('organizational_structure.phone')} />
            </Item>
          </Col>

          <Col xs={24} md={12} xl={8}>
            <Item
              name={['contactInfo', 'fax']}
              label={t('organizational_structure.fax')}
              rules={[
                {
                  pattern: PHONE_REGEX,
                  message: t('organizational_structure.phone_error')
                }
              ]}
            >
              <Input placeholder={t('organizational_structure.fax')} />
            </Item>
          </Col>

          <Col xs={24} md={12} xl={8}>
            <Item
              name={['contactInfo', 'email']}
              label={t('organizational_structure.email')}
              rules={requiredRules?.concat({ type: 'email' })}
            >
              <Input placeholder={t('organizational_structure.email')} />
            </Item>
          </Col>

          <Col xs={24} md={12} xl={6}>
            <Item
              name={['address', 'country']}
              label={t('other_institutions.country')}
            >
              <CountrySelect
                defaultValue="md"
                disabled
                placeholder={t('common.country')}
              />
            </Item>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Item
              name={['address', 'region']}
              label={t('organizational_structure.region')}
              rules={requiredRules}
            >
              <RegionSelect
                onChange={() => {
                  form.setFieldValue(['address', 'cuatmLocality'], null)
                }}
                placeholder={t('organizational_structure.region')}
              />
            </Item>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Item
              name={['address', 'cuatmLocality']}
              label={t('organizational_structure.city')}
              rules={requiredRules}
            >
              <CUATMTreeSelect
                placeholder={t('organizational_structure.city')}
                elementId={regionId || region}
                disabled={isDisabled || (!region && !regionId)}
              />
            </Item>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Item
              name={['address', 'street']}
              label={t('organizational_structure.street')}
              rules={requiredRules}
            >
              <Input placeholder={t('organizational_structure.street')} />
            </Item>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Item
              name={['address', 'unitNumber']}
              label={t('other_institutions.number')}
              rules={requiredRules}
            >
              <Input placeholder={t('other_institutions.number')} />
            </Item>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Item
              name="type"
              label={t('organizational_structure.institution_type')}
              rules={requiredRules}
            >
              <Select
                options={otherInstitutionOptions}
                placeholder={t('organizational_structure.institution_type')}
              />
            </Item>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Item
              name="status"
              label={`${t('counterparts.status')} (${status ? t('common.active') : t('common.inactive')})`}
              valuePropName="checked"
            >
              <Switch />
            </Item>
          </Col>
        </Row>
      </Form>
    </Card>
  )
}
